/**
 * Mint-currency resolution — the single source of truth for the currency a fresh session is
 * minted in. Deliberately APP-LEVEL (not under `nova/`, not inside a configurator flow) so the
 * permissive core `session` store slice can import it without inverting a store→nova dependency,
 * exactly like its sibling `@/utils/start-properties-market`.
 *
 * Currency is DECIDED at mint from the marketing-site MARKET hand-off (operating region `or`, plus an
 * optional explicit `currency`). After mint it can be switched between catalogue-ACTIVE currencies via
 * `POST /session/set-currency` (the account-menu selector, the region picker, and a different-currency
 * hand-off on resume all do this; see {@link handoffCurrencySwitch}). Minting in an INACTIVE currency
 * stays a one-way decision, since `/set-currency` rejects inactive codes, so the mint currency (the
 * whole session, bound + recommended campaigns, is priced in it) must be resolved correctly up front.
 *
 * The `/configurator` catalogue is the authoritative region→currency map, but it is provably NOT
 * loaded yet at mint time (the session mint races ahead of the catalogue fetch), so a static
 * fallback covering the tenant's launched (`is_active`) operating regions MUST cover the
 * null-catalogue case. The fallback is NOT an allowlist — a region absent from it simply has no
 * offline default (it resolves from the catalogue once loaded, or fails loud to USD at mint).
 */

import type { ConfiguratorConfigRoot } from '@/components/modules/spark/configurator/types';
import { readStartMarketHandoff } from '@/utils/start-properties-market';

/**
 * Static region→currency fallback, used when the `/configurator` catalogue has not loaded yet
 * (the mint-time race). Covers the tenant's launched (`is_active`) operating regions so a session
 * minted before the catalogue arrives still prices in the right currency. Kept in sync BY HAND with
 * the catalogue's `is_active` operating regions (their `region_data.currency_code`) — the authority
 * once loaded is `currencyForRegion`, this is only the offline default for the mint race.
 */
export const REGION_CURRENCY_FALLBACK: Readonly<Record<string, string>> = {
  INT: 'USD',
  MEX: 'MXN',
  BRA: 'BRL',
  ARG: 'ARS',
  CHL: 'CLP',
  COL: 'COP',
  PER: 'PEN',
  ZAF: 'ZAR',
  BOL: 'USD',
};

/**
 * Resolve the currency for an operating-region code.
 *
 * Precedence: the loaded catalogue's region entry (`region_data.currency_code`, else the region's
 * own `currency_code`) → the static {@link REGION_CURRENCY_FALLBACK} map → `null`. A region that
 * exists in the catalogue but carries no currency code still falls through to the static map, so a
 * partially-populated catalogue never yields `null` for a known market.
 */
export function currencyForRegion(
  or: string | null | undefined,
  catalog: ConfiguratorConfigRoot | null,
): string | null {
  if (!or) return null;
  if (catalog) {
    const region = catalog.operating_regions.find((r) => r.code === or);
    const code = region?.region_data?.currency_code ?? region?.currency_code;
    if (code) return code;
  }
  return REGION_CURRENCY_FALLBACK[or] ?? null;
}

/**
 * The currency a fresh session must mint in.
 *
 * Precedence: an explicit hand-off `currency` (the marketing site already committed one) →
 * {@link currencyForRegion} for the hand-off's operating region (catalogue if loaded, else the
 * static map) → `'USD'`. Never throws; a missing/invalid hand-off yields `'USD'`.
 *
 * Mint currency is a ONE-WAY decision (see the file header), so a region we cannot resolve is a
 * silent-corruption hazard, not a benign default: it mints USD for a non-USD market with no way
 * to correct it later. We therefore fail LOUD — a `console.error` naming the region — whenever a
 * non-empty operating region falls through to the blind `'USD'`, so a widened hand-off allowlist
 * that forgot to extend {@link REGION_CURRENCY_FALLBACK} surfaces in the console instead of
 * corrupting sessions. We do NOT throw (the mint must still proceed) and do NOT log for a
 * null/empty `or` (a genuinely anonymous landing legitimately defaults to USD).
 */
export function resolveMintCurrency(catalog: ConfiguratorConfigRoot | null): string {
  const { or, currency } = readStartMarketHandoff();
  if (currency) return currency;
  const resolved = currencyForRegion(or, catalog);
  if (resolved) return resolved;
  if (or) {
    // eslint-disable-next-line no-console
    console.error(
      `[configurator mint] unresolved currency for operating region "${or}"; defaulting to USD. Add it to REGION_CURRENCY_FALLBACK.`,
    );
  }
  return 'USD';
}

/**
 * The currency to switch an ALREADY-LOADED session to so it matches the marketing hand-off, or
 * `null` to leave it unchanged.
 *
 * {@link resolveMintCurrency} fixes a session's currency only once, on the FRESH mint. An owner who
 * first arrived in one market (e.g. International / USD) and later returns from another (e.g. the
 * BR / BRL site) with the SAME session/email RESUMES the original session, so the mint never re-runs
 * and the new hand-off currency has to be applied afterwards via `POST /session/set-currency`. This
 * returns the hand-off currency only when it is present, DIFFERS from the session's current currency,
 * and is one the catalogue flags active (the only currencies `/set-currency` accepts — an inactive
 * code would be rejected). Pure + side-effect-free so the decision is unit-testable without the store.
 */
export function handoffCurrencySwitch(
  handoffCurrency: string | null | undefined,
  sessionCurrency: string | null | undefined,
  isCatalogueActive: (code: string) => boolean,
): string | null {
  if (!handoffCurrency || handoffCurrency === sessionCurrency) return null;
  return isCatalogueActive(handoffCurrency) ? handoffCurrency : null;
}
